import { useCallback, useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate, useSearchParams } from "react-router-dom";

import PageHeader from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { SpinnerBlock } from "@/components/ui/spinner";
import { UnderlineTabs } from "@/components/ui/underline-tabs";
import TikTokIcon from "@/assets/tiktok";
import { usePermissions } from "@/contexts/PermissionsContext";
import { isTikTokFeatureOn, useTikTokAccess } from "@/hooks/useTikTokAccess";
import type { TikTokCreatorPick, TikTokFeatureKey } from "@/services/tiktokShop.service";
import { PlanFeature } from "@/types/permissions";

import { ComingSoonBadge, ComingSoonPanel } from "./pre-release";

import { InvitesTab } from "./invites/InvitesTab";
import { MessagesTab } from "./messages/MessagesTab";
import { SamplesTab } from "./samples/SamplesTab";
import { AdRequestsTab } from "./ad-requests/AdRequestsTab";

type Tab = "samples" | "ad-requests" | "invites" | "messages";
const TABS: Array<{ value: Tab; label: string }> = [
  { value: "samples", label: "Sample requests" },
  { value: "ad-requests", label: "Ad requests" },
  { value: "invites", label: "Collab invites" },
  { value: "messages", label: "Messages" },
];
/** PRE_RELEASE_TIKTOK: which server feature each tab waits on. */
const TAB_FEATURE: Record<Tab, Exclude<TikTokFeatureKey, "analytics">> = {
  samples: "samples",
  "ad-requests": "adRequests",
  invites: "invites",
  messages: "messages",
};

/** What another page hands over when it sends the brand here. */
export interface TikTokShopHandoff {
  inviteCreators?: TikTokCreatorPick[];
  messageCreator?: TikTokCreatorPick;
}

const readTab = (value: string | null): Tab =>
  value === "invites" || value === "messages" || value === "ad-requests" ? value : "samples";

/**
 * Working with TikTok Shop creators: free-sample requests, collab invites and
 * the affiliate inbox, on the brand's affiliate-app connection. Behind the
 * TikTok Shop flag and plan feature like every TikTok surface.
 */
export default function TikTokShopPage() {
  const { hasFeature, isLoading } = usePermissions();
  const access = useTikTokAccess();
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const tab = readTab(params.get("tab"));
  const [inviteCreators, setInviteCreators] = useState<TikTokCreatorPick[] | null>(null);
  const [messageCreator, setMessageCreator] = useState<TikTokCreatorPick | null>(null);

  const setTab = useCallback(
    (next: Tab) =>
      setParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          p.set("tab", next);
          return p;
        },
        { replace: true },
      ),
    [setParams],
  );

  // A handoff from search or another tab arrives once through router state.
  useEffect(() => {
    const handoff = location.state as TikTokShopHandoff | null;
    if (!handoff) return;
    if (handoff.inviteCreators?.length) setInviteCreators(handoff.inviteCreators);
    if (handoff.messageCreator) setMessageCreator(handoff.messageCreator);
    navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
  }, [location.state, location.pathname, location.search, navigate]);

  const startInvite = useCallback(
    (creators: TikTokCreatorPick[]) => {
      setInviteCreators(creators);
      setTab("invites");
    },
    [setTab],
  );
  const startMessage = useCallback(
    (creator: TikTokCreatorPick) => {
      setMessageCreator(creator);
      setTab("messages");
    },
    [setTab],
  );
  const clearInvite = useCallback(() => setInviteCreators(null), []);
  const clearMessage = useCallback(() => setMessageCreator(null), []);

  if (isLoading || access.isLoading) return <SpinnerBlock className="py-24" />;
  // No access (outside the PRE_RELEASE_TIKTOK pilot, or no plan feature at GA): the page does not exist.
  if (!access.enabled) return <Navigate to="/brand" replace />;

  // PRE_RELEASE_TIKTOK: tabs that wait on TikTok show a preview, no data calls.
  const tabOn = (t: Tab) => isTikTokFeatureOn(access.features, TAB_FEATURE[t]);
  const tabs = TABS.map((t) => ({
    value: t.value,
    label: tabOn(t.value) ? (
      t.label
    ) : (
      <span className="relative inline-block pr-14 pt-3">
        {t.label}
        <ComingSoonBadge floating size="sm" className="right-0 top-0" />
      </span>
    ),
  }));

  return (
    <div className="space-y-4">
      <PageHeader title="TikTok Shop creators" description="Review sample requests, send collab invites and message the creators who sell for you on TikTok." />
      {!hasFeature(PlanFeature.TIKTOK_SHOP) ? (
        <EmptyState
          icon={<TikTokIcon />}
          title="TikTok Shop is on the Scale plan"
          body="Upgrade to manage TikTok creators, samples and invites from Reveshare"
          action={
            <Button onClick={() => navigate("/brand/settings?tab=pricing")}>
              Upgrade to Scale
            </Button>
          }
        />
      ) : (
        <>
          <UnderlineTabs tabs={tabs} value={tab} onValueChange={setTab} />
          {!tabOn(tab) ? (
            <ComingSoonPanel feature={TAB_FEATURE[tab]} />
          ) : (
            <>
              {tab === "samples" && <SamplesTab onMessage={startMessage} onInvite={startInvite} />}
              {tab === "ad-requests" && <AdRequestsTab />}
              {tab === "invites" && <InvitesTab draftCreators={inviteCreators} onDraftConsumed={clearInvite} onMessage={startMessage} />}
              {tab === "messages" && <MessagesTab pendingCreator={messageCreator} onPendingConsumed={clearMessage} onInvite={startInvite} />}
            </>
          )}
        </>
      )}
    </div>
  );
}
